import React, { useEffect, useRef, useState } from "react";
import Particles from "./Particles";

/*
 * DUEL SCENE — parallax scroll animation.
 * The jagged \"cut\" from the hero continues here: layered mountain silhouettes
 * descend frame-by-frame as the user scrolls. Two swords slide in from the left
 * and right edges of the cut, cross in the middle like a duel, and on impact
 * a green spark burst fires at the intersection.
 */

const Sword = ({ side }) => (
  <svg
    viewBox="0 0 300 64"
    className="w-full h-full"
    xmlns="http://www.w3.org/2000/svg"
    style={side === "right" ? { transform: "scaleX(-1)" } : undefined}
  >
    <defs>
      <linearGradient id={`blade-${side}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f2f6ff" />
        <stop offset="0.5" stopColor="#c3cde2" />
        <stop offset="1" stopColor="#8b97b5" />
      </linearGradient>
      <linearGradient id={`guard-${side}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#6cf2a8" />
        <stop offset="1" stopColor="#25b566" />
      </linearGradient>
    </defs>
    {/* pommel */}
    <circle cx="14" cy="32" r="9" fill="url(#guard-{side})" />
    <circle cx="14" cy="32" r="9" fill={`url(#guard-${side})`} />
    <circle cx="14" cy="32" r="3.5" fill="#12151f" opacity="0.5" />
    {/* handle */}
    <rect x="22" y="26" width="36" height="12" rx="5" fill="#2b3346" />
    <rect x="27" y="26" width="4" height="12" fill="#39445e" />
    <rect x="37" y="26" width="4" height="12" fill="#39445e" />
    <rect x="47" y="26" width="4" height="12" fill="#39445e" />
    {/* guard */}
    <path d="M58 12 L70 12 Q74 32 70 52 L58 52 Q64 32 58 12 Z" fill={`url(#guard-${side})`} />
    {/* blade */}
    <path
      d="M72 22 L268 26 L294 32 L268 38 L72 42 Q78 32 72 22 Z"
      fill={`url(#blade-${side})`}
    />
    {/* blade center line */}
    <path d="M76 32 L286 32" stroke="#77839f" strokeWidth="1.4" opacity="0.8" />
    {/* edge glint */}
    <path d="M84 25.5 L250 28.5" stroke="#ffffff" strokeWidth="1.6" opacity="0.55" strokeLinecap="round" />
  </svg>
);

/* mountain layer paths — irregular jagged cuts, more sky left open on top */
const LAYERS = [
  {
    d: "M0,110 L90,60 150,95 260,35 340,80 430,55 540,20 640,85 720,50 830,95 900,40 1010,75 1100,30 1200,88 1290,58 1380,92 1440,45 L1440,180 L0,180 Z",
    fill: "#2b3348",
    speed: 0.3,
    top: "16%",
  },
  {
    d: "M0,85 L70,55 160,100 250,45 330,90 440,25 520,75 630,40 740,95 820,35 930,80 1020,55 1130,22 1230,85 1330,50 1440,78 L1440,180 L0,180 Z",
    fill: "#252c3f",
    speed: 0.48,
    top: "31%",
  },
  {
    d: "M0,95 L100,40 190,85 280,60 370,105 470,30 570,80 680,50 770,100 870,38 970,90 1080,60 1180,25 1280,95 1370,65 1440,90 L1440,180 L0,180 Z",
    fill: "#1f2536",
    speed: 0.68,
    top: "45%",
  },
  {
    d: "M0,70 L80,45 180,95 290,30 380,75 490,55 590,100 690,35 790,85 890,60 990,25 1090,80 1190,45 1290,100 1390,55 1440,70 L1440,180 L0,180 Z",
    fill: "#191e2c",
    speed: 0.9,
    top: "59%",
  },
  {
    d: "M0,90 L110,50 200,100 300,40 410,90 500,28 610,75 720,45 810,95 920,35 1030,85 1130,55 1240,20 1340,90 1440,60 L1440,180 L0,180 Z",
    fill: "#12151f",
    speed: 1.15,
    top: "73%",
  },
];

const SPARKS = Array.from({ length: 14 }, (_, i) => {
  const angle = (i / 14) * Math.PI * 2;
  const dist = 60 + (i % 4) * 34;
  return {
    tx: Math.cos(angle) * dist,
    ty: Math.sin(angle) * dist * 0.85,
    delay: (i % 5) * 0.04,
    size: i % 3 === 0 ? 7 : 4,
  };
});

/* twinkling stars scattered across the upper sky */
const STARS = Array.from({ length: 46 }, () => ({
  left: Math.random() * 100,
  top: Math.random() * 50,
  size: Math.random() < 0.22 ? 2.6 : 1.6,
  dur: 2.5 + Math.random() * 4,
  delay: Math.random() * 5,
}));

/* drifting mist bands woven between the mountain cuts */
const MISTS = [
  { top: "32%", z: 2, dur: "38s", o: 0.1, rev: false },
  { top: "48%", z: 3, dur: "52s", o: 0.14, rev: true },
  { top: "63%", z: 4, dur: "44s", o: 0.17, rev: false },
];

const DuelScene = () => {
  const sectionRef = useRef(null);
  const [clashed, setClashed] = useState(false);
  const clashedRef = useRef(false);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = sectionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = el.offsetHeight - window.innerHeight;
        const p = Math.min(1, Math.max(0, -rect.top / total));
        el.style.setProperty("--p", p.toFixed(4));
        // sword progress: swords travel between 15% and 70% of the scene
        const sp = Math.min(1, Math.max(0, (p - 0.15) / 0.55));
        // ease-out cubic for a heavier, weapon-like motion
        const eased = 1 - Math.pow(1 - sp, 3);
        el.style.setProperty("--sp", eased.toFixed(4));
        const hit = sp >= 0.995;
        if (hit !== clashedRef.current) {
          clashedRef.current = hit;
          setClashed(hit);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="duel-scene"
      ref={sectionRef}
      className="relative"
      style={{ height: "260vh", "--p": 0, "--sp": 0 }}
      data-testid="duel-scene"
    >
      <div className="sticky top-0 h-screen overflow-hidden duel-sky">
        <div className={`absolute inset-0 ${clashed ? "clash-shake" : ""}`}>
        {/* twinkling stars in the upper sky */}
        {STARS.map((s, i) => (
          <span
            key={`star-${i}`}
            className="duel-star"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              "--dur": `${s.dur}s`,
              "--delay": `${s.delay}s`,
            }}
          />
        ))}

        {/* green moon rising behind the mountains */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: "64%",
            top: "3%",
            zIndex: 0,
            transform: "translateY(calc((1 - var(--p)) * 90px))",
          }}
          data-testid="duel-moon"
        >
          <div className="duel-moon" />
        </div>

        {/* horizon glow deep in the valley */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-[1100px] h-[520px] rounded-full blur-[110px] bg-[#3ddc84] pointer-events-none"
          style={{ top: "46%", opacity: "calc(0.06 + var(--p) * 0.14)" }}
        />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        {/* green particles rising smoothly between the cut layers */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 6 }}>
          <Particles count={28} />
        </div>

        {/* parallax mountain cut layers, descending frame by frame */}
        {LAYERS.map((l, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              top: l.top,
              height: "46%",
              transform: `translateY(calc(var(--p) * ${-140 * l.speed}px))`,
              zIndex: i + 1,
            }}
          >
            <svg
              viewBox="0 0 1440 180"
              preserveAspectRatio="none"
              className="w-full h-full block"
            >
              <path d={l.d} fill={l.fill} />
            </svg>
          </div>
        ))}

        {/* drifting mist bands between the cuts */}
        {MISTS.map((m, i) => (
          <div
            key={`mist-${i}`}
            className="duel-mist absolute pointer-events-none"
            style={{
              top: m.top,
              zIndex: m.z,
              opacity: m.o,
              animationDuration: m.dur,
              animationDirection: m.rev ? "reverse" : "normal",
            }}
          />
        ))}

        {/* ================= SWORDS ================= */}
        <div
          className="absolute left-0 right-0 pointer-events-none"
          style={{ top: "48%", zIndex: 10 }}
        >
          {/* left sword — slides in from the left, angles up into the cross */}
          <div
            className="absolute left-1/2 top-0 w-[300px] sm:w-[420px] h-[64px] sm:h-[90px] origin-center"
            style={{
              transform: `
                translateX(calc(-50% + (1 - var(--sp)) * -60vw))
                translateY(-50%)
                rotate(calc(-8deg + var(--sp) * -22deg))
              `,
              filter: clashed
                ? "drop-shadow(0 0 18px rgba(61,220,132,0.65))"
                : "drop-shadow(0 6px 14px rgba(0,0,0,0.5))",
              transition: "filter 0.25s ease",
            }}
            data-testid="sword-left"
          >
            <Sword side="left" />
          </div>
          {/* right sword — mirrored */}
          <div
            className="absolute left-1/2 top-0 w-[300px] sm:w-[420px] h-[64px] sm:h-[90px] origin-center"
            style={{
              transform: `
                translateX(calc(-50% + (1 - var(--sp)) * 60vw))
                translateY(-50%)
                rotate(calc(8deg + var(--sp) * 22deg))
              `,
              filter: clashed
                ? "drop-shadow(0 0 18px rgba(61,220,132,0.65))"
                : "drop-shadow(0 6px 14px rgba(0,0,0,0.5))",
              transition: "filter 0.25s ease",
            }}
            data-testid="sword-right"
          >
            <Sword side="right" />
          </div>

          {/* ================ SPARK BURST at the intersection ================ */}
          <div
            className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2"
            style={{ zIndex: 12 }}
          >
            <div className={`spark-core ${clashed ? "spark-active" : ""}`} data-testid="spark-core" />
            <div className={`spark-ring ${clashed ? "spark-active" : ""}`} />
            {SPARKS.map((s, i) => (
              <span
                key={i}
                className={`spark-particle ${clashed ? "spark-active" : ""}`}
                style={{
                  "--tx": `${s.tx}px`,
                  "--ty": `${s.ty}px`,
                  "--d": `${s.delay}s`,
                  width: s.size,
                  height: s.size,
                }}
              />
            ))}
          </div>
        </div>

        {/* reveal text after the clash */}
        <div
          className="absolute left-0 right-0 text-center pointer-events-none px-6"
          style={{
            top: "64%",
            zIndex: 11,
            opacity: "calc((var(--p) - 0.72) * 4)",
            transform: "translateY(calc((1 - var(--p)) * 60px))",
          }}
        >
          <p className="uppercase text-xs font-bold tracking-[0.3em] text-[#3ddc84] mb-3">
            The duel is set
          </p>
          <h2 className="font-display font-extrabold italic text-3xl sm:text-5xl text-white uppercase tracking-tight">
            Claim your edge
          </h2>
          <p className="mt-4 text-sm text-[#97a1b8] max-w-md mx-auto">
            Keep scrolling — the juiciest bonuses in the arena are waiting below.
          </p>
        </div>

        </div>

        {/* clash flash — sky flickers green on impact */}
        <div
          className={`absolute inset-0 pointer-events-none ${clashed ? "clash-flash" : ""}`}
          style={{
            zIndex: 12,
            opacity: 0,
            background:
              "radial-gradient(circle at 50% 52%, rgba(108,242,168,0.45), rgba(61,220,132,0.12) 45%, transparent 75%)",
          }}
          data-testid="clash-flash"
        />

        {/* bottom fade into the bonuses section */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[160px] pointer-events-none"
          style={{
            zIndex: 13,
            background: "linear-gradient(180deg, rgba(18,21,31,0) 0%, #171b26 92%)",
          }}
        />
      </div>
    </section>
  );
};

export default DuelScene;
